import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Col, Row } from 'react-bootstrap';
import DataLoadError from '@/components/errors/DataLoadError';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import { KpiStatCard } from '../components/common/KpiStatCard';
import { VisibilityPageSkeleton } from '../components/common/Skeleton';
import { NoProject } from '../components/NoProject';
import { VisibilityTrendChart } from '../components/visibility/VisibilityTrendChart';
import { PageFilterBar } from '../components/filters/PageFilterBar';
import { BrandSourcesDrawer } from '../components/sources/BrandSourcesDrawer';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { getHelpHtml } from '../utils/help/help-content';
import {
  getVisibilityBrandColumns,
  providerBreakdownColumns,
  visibilityPromptPerformanceColumns,
  type VisibilityBrandRow,
  type VisibilityPromptRow
} from '../config/visibilityColumns';
import { useProject } from '../context/ProjectContext';
import { useTrackerAiPath } from '../utils/paths';
import { buildDateRangeQueryOptions } from '../utils/visibility-range';
import {
  filterTrendByDomain,
  getDomainScopedSummaryKpis,
  getTrendBrandKeysForFilter,
  resolveVisibilityTopBrand
} from '../utils/domain-filter-utils';
import { resolveAiMentions, formatAiUsagePercent } from '../utils/visibility-metrics';
import {
  findOwnBrandRow,
  formatPromptCoverageValue,
  resolvePromptCoverageKpi,
  resolveVisibilityScoreKpi,
  sortVisibilityBrands
} from '../utils/visibility-page-utils';
import { computePromptDomainMetrics, getPromptBrandRows } from '../utils/prompt-domain-metrics';
import { useProjectCompetitors } from '../hooks/useProjectCompetitors';
import { useProjectEverExecuted } from '../hooks/useProjectEverExecuted';
import type {
  ProjectVisibilitySummary,
  ProjectVisibilityTopBrand,
  VisibilityTrendResponse
} from '../types';

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  settings?: Record<string, unknown>;
  otherOptions?: Record<string, unknown>;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

export function VisibilityPage() {
  return (
    <NoProject>
      <VisibilityContent />
    </NoProject>
  );
}

function VisibilityContent() {
  const {
    projectId,
    topicFilterId,
    visibilityRange,
    modelFilterId,
    customFrom,
    customTo,
    domainFilterId,
    sourceFilterIds
  } = useProject();
  const competitors = useProjectCompetitors(projectId);
  const navigate = useNavigate();
  const toTrackerAi = useTrackerAiPath();
  const rangeQuery = useMemo(
    () =>
      buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
        topicId: topicFilterId ?? undefined,
        providerIds: modelFilterId ? [modelFilterId] : undefined,
        sourceIds: sourceFilterIds.length > 0 ? sourceFilterIds : undefined
      }),
    [visibilityRange, customFrom, customTo, topicFilterId, modelFilterId, sourceFilterIds]
  );
  const [summary, setSummary] = useState<ProjectVisibilitySummary | null>(null);
  const [trend, setTrend] = useState<VisibilityTrendResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [sourcesBrand, setSourcesBrand] = useState<ProjectVisibilityTopBrand | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const data = await api.getProjectVisibilitySummary(projectId!, rangeQuery);
        const topKeys = getTrendBrandKeysForFilter(data, domainFilterId, competitors);
        const trendData = await api.getProjectVisibilityTrend(projectId!, {
          ...rangeQuery,
          brandKeys: topKeys?.length ? topKeys : undefined
        });
        if (!cancelled) {
          setSummary(data);
          setTrend(trendData);
          setError(false);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setSummary(null);
          setTrend(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId, rangeQuery, domainFilterId, competitors, retryNonce]);

  const executedPromptRows = useMemo(
    () =>
      (summary?.promptPerformance ?? []).filter(
        item => item.status === 'executed' || item.status === 'partial'
      ),
    [summary]
  );

  const domainKpis = useMemo(
    () =>
      summary
        ? getDomainScopedSummaryKpis(summary, domainFilterId, competitors)
        : { visibilityScore: null, avgPosition: null, aiMentions: null, totalMentions: null },
    [summary, domainFilterId, competitors]
  );

  const domainBrand = useMemo(
    () =>
      summary && domainFilterId !== 'all'
        ? resolveVisibilityTopBrand(summary, domainFilterId, competitors)
        : null,
    [summary, domainFilterId, competitors]
  );

  const filteredTrend = useMemo(
    () => filterTrendByDomain(trend, domainFilterId, competitors),
    [trend, domainFilterId, competitors]
  );

  const domainKpiSubtext = domainBrand
    ? `برای ${domainBrand.canonicalName}`
    : domainFilterId !== 'all'
      ? 'داده‌ای برای دامنه انتخاب‌شده نیست'
      : undefined;

  const ownBrand = summary?.ownBrand ?? null;
  const ownBrandRow = useMemo(
    () => (summary ? findOwnBrandRow(summary.topBrands) : null),
    [summary]
  );
  const useOwnBrandKpis = domainFilterId === 'all';
  const promptCoverageKpi = useMemo(
    () =>
      resolvePromptCoverageKpi({
        ownBrand,
        ownBrandRow,
        domainBrand,
        useOwnBrandScope: useOwnBrandKpis
      }),
    [ownBrand, ownBrandRow, domainBrand, useOwnBrandKpis]
  );
  const visibilityKpi = useMemo(
    () =>
      resolveVisibilityScoreKpi({
        ownBrand,
        ownBrandRow,
        domainBrand,
        domainVisibilityScore: domainKpis.visibilityScore,
        useOwnBrandScope: useOwnBrandKpis
      }),
    [ownBrand, ownBrandRow, domainBrand, domainKpis.visibilityScore, useOwnBrandKpis]
  );
  const aiMentionsKpi = useOwnBrandKpis ? ownBrand?.aiMentions : domainKpis.aiMentions;
  const avgPositionKpi = useOwnBrandKpis ? ownBrand?.avgPosition : domainKpis.avgPosition;

  const brandRows = useMemo(
    () => sortVisibilityBrands(summary?.topBrands ?? []),
    [summary]
  );

  const executions = summary?.summary.executions ?? 0;
  const coverageDenominator = summary
    ? summary.summary.executedPrompts || summary.summary.prompts
    : 0;

  const brandTableRows: VisibilityBrandRow[] = useMemo(
    () =>
      brandRows.map(brand => ({
        ...brand,
        aiMentionsResolved: resolveAiMentions(brand),
        aiUsageLabel: formatAiUsagePercent(resolveAiMentions(brand), executions),
        promptCoverageLabel: `${brand.promptCoverage}/${coverageDenominator || '—'}`
      })),
    [brandRows, executions, coverageDenominator]
  );

  const promptTableRows: VisibilityPromptRow[] = useMemo(
    () =>
      executedPromptRows.map(item => {
        const metrics = computePromptDomainMetrics(
          getPromptBrandRows(item),
          domainFilterId,
          competitors
        );
        return { ...item, computedVisibility: metrics.visibility };
      }),
    [executedPromptRows, domainFilterId, competitors]
  );

  const brandColumns = useMemo(
    () => getVisibilityBrandColumns(row => setSourcesBrand(row)),
    []
  );

  const { everExecuted, checking } = useProjectEverExecuted(
    projectId,
    summary?.summary.executions ?? 0,
    summary != null,
  );

  if (error) {
    return (
      <div>
        <div className="mb-4">
          <PageFilterBar showSources splitDateAndFilters />
        </div>
        <DataLoadError
          onRetry={() => setRetryNonce(n => n + 1)}
          isRetrying={loading}
        />
      </div>
    );
  }

  if ((loading && (!summary || !everExecuted)) || checking) {
    return (
      <div>
        <div className="mb-4">
          <PageFilterBar showSources splitDateAndFilters />
        </div>
        <VisibilityPageSkeleton />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <PageFilterBar showSources splitDateAndFilters />
      </div>

      {!summary || !everExecuted ? (
        <EmptyVisibilityState onGoToPrompts={() => navigate(toTrackerAi('prompts'))} />
      ) : (
        <div className="d-flex flex-column gap-4">
          <Row className="g-3">
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={0}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    دیده‌شدن در AI
                    <HelpTooltip html={getHelpHtml('ai_visibility')} />
                  </span>
                }
                value={visibilityKpi.score != null ? `${visibilityKpi.score}%` : '—'}
                valueClassName="text-warning"
                badgeText={
                  domainFilterId === 'all' && visibilityKpi.delta != null
                    ? `${visibilityKpi.delta > 0 ? '+' : ''}${visibilityKpi.delta}%`
                    : undefined
                }
                badgeBg={
                  visibilityKpi.delta == null
                    ? 'secondary'
                    : visibilityKpi.delta > 0
                      ? 'success'
                      : visibilityKpi.delta < 0
                        ? 'danger'
                        : 'secondary'
                }
                subtext={domainKpiSubtext ?? formatDeltaSubtext(visibilityKpi.delta ?? null)}
              />
            </Col>
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={1}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    منشن در AI
                    <HelpTooltip html={getHelpHtml('ai_mentions')} />
                  </span>
                }
                value={aiMentionsKpi ?? '—'}
                valueClassName="text-info"
                subtext={domainKpiSubtext ?? 'برند خودتان در پاسخ‌های AI'}
              />
            </Col>
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={2}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    پوشش پرامپت
                    <HelpTooltip html={getHelpHtml('prompt_coverage')} />
                  </span>
                }
                value={formatPromptCoverageValue(
                  promptCoverageKpi.coverage,
                  summary!.summary.executedPrompts,
                  summary!.summary.prompts
                )}
                valueClassName="text-primary"
                subtext={
                  promptCoverageKpi.brandName
                    ? `جایی که ${promptCoverageKpi.brandName} ظاهر شد`
                    : 'نام‌های مستعار برند خود را تنظیم کنید'
                }
              />
            </Col>
            <Col xs={6} xl={3}>
              <KpiStatCard
                imageIndex={3}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    میانگین رتبه AI
                    <HelpTooltip html={getHelpHtml('avg_ai_position')} />
                  </span>
                }
                value={avgPositionKpi != null ? `#${avgPositionKpi}` : '—'}
                valueClassName="text-success"
                subtext={domainKpiSubtext ?? 'میانگین رتبه برند خودتان'}
              />
            </Col>
          </Row>

          <Card>
            <FalconCardHeader
              light
              titleTag="h6"
              className="py-3"
              title={
                <div>
                  <span className="d-inline-flex align-items-center gap-1">
                    ترند دیده‌شدن
                    <HelpTooltip html={getHelpHtml('visibility_trend')} />
                  </span>
                  <p className="fs--2 text-700 mb-0 fw-normal mt-2">
                    ۵ برند برتر بر اساس امتیاز دیده‌شدن
                  </p>
                </div>
              }
            />
            <Card.Body>
              <VisibilityTrendChart trend={filteredTrend} />
            </Card.Body>
          </Card>

          <Card className="overflow-hidden ag-grid-in-card">
            <FalconCardHeader
              light
              titleTag="h6"
              className="py-3"
              title={
                <div>
                  <span className="d-inline-flex align-items-center gap-1">
                    دیده‌شدن برند
                    <HelpTooltip html={getHelpHtml('brand_visibility')} />
                  </span>
                  <p className="fs--2 text-700 mb-0 fw-normal mt-2">
                    مرتب‌شده بر اساس امتیاز دیده‌شدن، سپس میانگین رتبه
                  </p>
                </div>
              }
            />
            <Card.Body>
              <TypedAgGridWrapper
                table_name="tracker_ai_visibility_brands"
                columns={brandColumns}
                data={brandTableRows}
                height="480px"
                settings={{
                  pagination: true,
                  fullscreen: false,
                  changeHeight: false,
                  saveGrid: false,
                  resetGrid: false,
                  sideBar: false
                }}
              />
            </Card.Body>
          </Card>

          <Row className="g-4">
            <Col xl={6}>
              <Card className="h-100 overflow-hidden ag-grid-in-card">
                <FalconCardHeader
                  light
                  titleTag="h6"
                  className="py-3"
                  title={
                    <div>
                      <span className="d-inline-flex align-items-center gap-1">
                        عملکرد پرامپت
                        <HelpTooltip html={getHelpHtml('prompt_performance')} />
                      </span>
                      <p className="fs--2 text-700 mb-0 fw-normal mt-2">
                        مرتب‌شده بر اساس تأثیر دیده‌شدن
                      </p>
                    </div>
                  }
                />
                <Card.Body>
                  <TypedAgGridWrapper
                    table_name="tracker_ai_visibility_prompts"
                    columns={visibilityPromptPerformanceColumns}
                    data={promptTableRows}
                    height="420px"
                    otherOptions={{
                      onRowClicked: (event: { data?: VisibilityPromptRow }) => {
                        if (event.data?.promptId) {
                          navigate(toTrackerAi('prompts', `prompt=${event.data.promptId}`));
                        }
                      },
                      rowClass: 'cursor-pointer'
                    }}
                    settings={{
                      pagination: true,
                      fullscreen: false,
                      changeHeight: false,
                      saveGrid: false,
                      resetGrid: false,
                      sideBar: false
                    }}
                  />
                </Card.Body>
              </Card>
            </Col>

            <Col xl={6}>
              <Card className="h-100 overflow-hidden ag-grid-in-card">
                <FalconCardHeader
                  light
                  titleTag="h6"
                  className="py-3"
                  title={
                    <div>
                      <span className="d-inline-flex align-items-center gap-1">
                        مقایسه پرووایدر
                        <HelpTooltip html={getHelpHtml('provider_comparison')} />
                      </span>
                      <p className="fs--2 text-700 mb-0 fw-normal mt-2">
                        عملکرد مدل‌های AI در این بازه
                      </p>
                    </div>
                  }
                />
                <Card.Body>
                  <TypedAgGridWrapper
                    table_name="tracker_ai_visibility_providers"
                    columns={providerBreakdownColumns}
                    data={summary!.providerBreakdown}
                    height="420px"
                    settings={{
                      pagination: true,
                      fullscreen: false,
                      changeHeight: false,
                      saveGrid: false,
                      resetGrid: false,
                      sideBar: false
                    }}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      )}

      {projectId && (
        <BrandSourcesDrawer
          open={sourcesBrand != null}
          brand={sourcesBrand}
          projectId={projectId}
          rangeQuery={rangeQuery}
          onClose={() => setSourcesBrand(null)}
        />
      )}
    </div>
  );
}

function EmptyVisibilityState({ onGoToPrompts }: { onGoToPrompts: () => void }) {
  return (
    <Card className="border-dashed">
      <Card.Body className="py-5 text-center">
        <div
          className="mx-auto mb-3 d-inline-flex align-items-center justify-content-center rounded-3 bg-primary-subtle text-primary"
          style={{ width: 48, height: 48 }}
        >
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M3 13.5l3.5-3.5 4 4L21 4.5M3 19.5h18"
            />
          </svg>
        </div>
        <h2 className="fs-1 fw-semibold text-1000">هنوز داده دیده‌شدنی وجود ندارد</h2>
        <p className="mx-auto mt-2 fs--1 text-500" style={{ maxWidth: '28rem' }}>
          پرامپت‌ها را اجرا کنید تا بینش‌های دیده‌شدن را ببینید.
        </p>
        <button type="button" onClick={onGoToPrompts} className="btn btn-primary mt-3">
          رفتن به پرامپت‌ها
        </button>
      </Card.Body>
    </Card>
  );
}

function formatDeltaSubtext(delta: number | null): string {
  if (delta == null) return 'مقایسه با بازه قبلی';
  if (delta > 0) return `+${delta}% نسبت به بازه قبلی`;
  if (delta < 0) return `${delta}% نسبت به بازه قبلی`;
  return 'بدون تغییر نسبت به بازه قبلی';
}

export default VisibilityPage;
